import { useEffect, useState, useMemo } from "react";
import Alert from "components/common/Alert";
import { MdDelete } from "react-icons/md";
import { getExhibitor } from "api/exhibitor.api";
import { useEventCategory } from "components/eventCategory/hooks/useEventCategory";
import { fetchOrganizerEvents } from "api/events.api";
import { getImageUrl } from "utils/imageHelper";
import PageLayout from ".././PageFromLayout";

export default function ExhibitorEventForm({
  initialData = null,
  onSubmit,
  onBack,
}) {
  const [exhibitors, setExhibitors] = useState([]);
  const [events, setEvents] = useState([]);
  const [alert, setAlert] = useState("");
  const { categories, loadCategoriesByOrganizer } = useEventCategory();
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    exhibitor_id: "",
    parent_event_id: "",
    event_name: "",
    event_dates: [{ from_date: "", to_date: "" }],
    location: "",
    description: "",
    images: [],
    eventImage: [],
    eventVideo: [],
    default_media: "",
  });

  const normalizePath = (path) => {
    if (!path || typeof path !== "string") return "";
    let cleaned = path
      .replace(/\\/g, "/")
      .replace(/\/{2,}/g, "/")
      .trim();
    cleaned = cleaned.replace(":/", "://");
    return cleaned;
  };

  const defaultMeta = useMemo(() => {
    if (!formData.default_media) return null;

    if (typeof formData.default_media === "object") {
      return formData.default_media;
    }

    try {
      return JSON.parse(formData.default_media);
    } catch {
      return null;
    }
  }, [formData.default_media]);

  const isDefaultMedia = (item, index, mediaType) => {
    if (!defaultMeta) return false;

    const isExisting = typeof item === "string";

    if (defaultMeta.type === "image" || defaultMeta.type === "video") {
      return (
        defaultMeta.type === mediaType &&
        isExisting &&
        normalizePath(defaultMeta.value) === normalizePath(item)
      );
    }

    if (defaultMeta.mediaType === mediaType) {
      if (defaultMeta.type === "existing" && isExisting) {
        return normalizePath(defaultMeta.value) === normalizePath(item);
      }
      if (defaultMeta.type === "new" && !isExisting) {
        return defaultMeta.value === index;
      }
    }

    return false;
  };

  useEffect(() => {
    const companyId = localStorage.getItem("company_id");

    getExhibitor(companyId)
      .then((res) => setExhibitors(res.data || []))
      .catch(() => setExhibitors([]));

    fetchOrganizerEvents(companyId)
      .then((res) => {
        const mainEvents =
          res.data?.data?.filter((e) => e.event_type === "main") || [];
        setEvents(mainEvents);
      })
      .catch(() => setEvents([]));

    loadCategoriesByOrganizer(companyId);
  }, []);

  useEffect(() => {
    if (!initialData) return;

    setFormData({
      exhibitor_id: initialData.exhibitor_id?._id || "",
      parent_event_id: initialData.parent_event_id?._id || "",
      event_category_id:
        initialData.event_category_id?._id ||
        initialData.event_category_id ||
        "",
      event_name: initialData.event_name || "",
      event_dates: initialData.event_dates?.length
        ? initialData.event_dates
        : [{ from_date: "", to_date: "" }],
      location: initialData.location || "",
      description: initialData.description || "",
      images: initialData.images || [],
      eventImage: initialData.eventImage || [],
      eventVideo: initialData.eventVideo || [],
      default_media: initialData.default_media || "",
    });
  }, [initialData]);

  const handleImages = (e) => {
    const files = Array.from(e.target.files);
    setFormData((p) => ({ ...p, images: [...p.images, ...files] }));
  };

  const removeImage = (index) => {
    setFormData((p) => {
      const updated = [...p.eventImage];
      const removed = updated.splice(index, 1)[0];

      let newDefault = p.default_media;

      try {
        const parsed =
          typeof p.default_media === "object"
            ? p.default_media
            : JSON.parse(p.default_media || "{}");

        const removedPath = normalizePath(removed);

        const isBackendDefault =
          parsed.type === "image" &&
          normalizePath(parsed.value) === removedPath;

        const isFrontendDefault =
          parsed.mediaType === "image" &&
          ((parsed.type === "existing" &&
            normalizePath(parsed.value) === removedPath) ||
            (parsed.type === "new" && parsed.value === index));

        if (isBackendDefault || isFrontendDefault) {
          newDefault = "";
        }
      } catch {}

      return { ...p, eventImage: updated, default_media: newDefault };
    });
  };

  const removeVideo = (index) => {
    setFormData((p) => {
      const updated = [...p.eventVideo];
      updated.splice(index, 1);
      return { ...p, eventVideo: updated };
    });
  };

  const setDefaultMedia = (config) => {
    setFormData((prev) => ({
      ...prev,
      default_media: JSON.stringify({
        type: config.type,
        mediaType: config.mediaType,
        value: config.value,
      }),
    }));
  };

  const addDateSlot = () => {
    setFormData((prev) => ({
      ...prev,
      event_dates: [...prev.event_dates, { from_date: "", to_date: "" }],
    }));
  };

  const removeDateSlot = (index) => {
    setFormData((prev) => {
      const updated = [...prev.event_dates];
      updated.splice(index, 1);
      return { ...prev, event_dates: updated };
    });
  };

  const handleDateChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.event_dates];
      updated[index][field] = value;
      return { ...prev, event_dates: updated };
    });
  };

  const formatForInput = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleMultimedia = (e) => {
    const files = Array.from(e.target.files);
    const images = [];
    const videos = [];
    files.forEach((file) => {
      if (file.type.startsWith("image/")) images.push(file);
      else if (file.type.startsWith("video/")) videos.push(file);
    });
    setFormData((prev) => ({
      ...prev,
      eventImage: [...prev.eventImage, ...images],
      eventVideo: [...prev.eventVideo, ...videos],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();

    if (
      !formData.exhibitor_id ||
      !formData.parent_event_id ||
      !formData.event_category_id ||
      !formData.event_name
    ) {
      setAlert("Please fill all required fields");
      return;
    }

    const fd = new FormData();

    const existingImages = formData.eventImage.filter(
      (img) => typeof img === "string"
    );
    const existingVideos = formData.eventVideo.filter(
      (vid) => typeof vid === "string"
    );

    fd.append("existing_eventImage", JSON.stringify(existingImages));
    fd.append("existing_eventVideo", JSON.stringify(existingVideos));

    formData.eventImage.forEach((img) => {
      if (img instanceof File) fd.append("eventImage", img);
    });

    formData.eventVideo.forEach((vid) => {
      if (vid instanceof File) fd.append("eventVideo", vid);
    });

    if (formData.default_media) {
      fd.append(
        "default_media",
        typeof formData.default_media === "object"
          ? JSON.stringify(formData.default_media)
          : formData.default_media
      );
    }

    fd.append("exhibitor_id", formData.exhibitor_id);
    fd.append("parent_event_id", formData.parent_event_id);
    fd.append("event_category_id", formData.event_category_id);
    fd.append("event_name", formData.event_name);
    fd.append("location", formData.location);
    fd.append("description", formData.description);

    const formattedDates = formData.event_dates.map((slot) => ({
      from_date: new Date(slot.from_date).toISOString(),
      to_date: new Date(slot.to_date).toISOString(),
    }));
    fd.append("event_dates", JSON.stringify(formattedDates));

    fd.append(
      "existing_images",
      JSON.stringify(formData.images.filter((img) => typeof img === "string"))
    );
    formData.images.forEach((img) => {
      if (img instanceof File) fd.append("images", img);
    });

    setSubmitting(true);
    try {
      await onSubmit(fd);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageLayout
      title={initialData ? "Edit Exhibitor Event" : "Create Exhibitor Event"}
      subtitle={
        initialData
          ? "Update the exhibitor event details below."
          : "Add a new exhibitor event to the platform. Fill in the details below."
      }
    >
      <form onSubmit={submit} className="card-form">
        {alert && <Alert type="error" message={alert} />}

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <Select
            label="Exhibitor"
            value={formData.exhibitor_id}
            onChange={(e) =>
              setFormData({ ...formData, exhibitor_id: e.target.value })
            }
            required
          >
            <option value="">Select Exhibitor</option>
            {exhibitors.map((e) => (
              <option key={e._id} value={e._id}>
                {e.title}
              </option>
            ))}
          </Select>

          <Select
            label="Main Event"
            value={formData.parent_event_id}
            onChange={(e) =>
              setFormData({ ...formData, parent_event_id: e.target.value })
            }
            required
          >
            <option value="">Select Main Event</option>
            {events.map((ev) => (
              <option key={ev._id} value={ev._id}>
                {ev.event_name}
              </option>
            ))}
          </Select>

          <Select
            label="Event Category"
            value={formData.event_category_id}
            required
            onChange={(e) =>
              setFormData((p) => ({ ...p, event_category_id: e.target.value }))
            }
          >
            <option value="">Select Category</option>
            {categories.map((cat) => (
              <option key={cat._id} value={cat._id}>
                {cat.title}
              </option>
            ))}
          </Select>

          <Input
            label="Sub Event Name"
            value={formData.event_name}
            onChange={(e) =>
              setFormData({ ...formData, event_name: e.target.value })
            }
            required
          />

          <Input
            label="Location"
            value={formData.location}
            onChange={(e) =>
              setFormData({ ...formData, location: e.target.value })
            }
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium">Description</label>
          <textarea
            className="w-full rounded-xl border p-3"
            rows={4}
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
          />
        </div>

        <div className="mt-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-xl font-semibold text-gray-800">Event Dates</h3>
            <button
              type="button"
              onClick={addDateSlot}
              className="rounded-lg bg-brand-500 px-4 py-2 text-sm text-white transition hover:bg-brand-600"
            >
              + Add Days
            </button>
          </div>

          <div className="relative rounded-2xl border bg-white p-5 shadow-sm">
            {formData.event_dates.map((slot, index) => (
              <div
                key={index}
                className="relative rounded-2xl border border-gray-200 bg-gray-50 p-5 shadow-sm transition hover:shadow-md"
              >
                <div className="absolute -top-3 left-4 rounded-full bg-brand-500 px-3 py-1 text-xs text-white shadow">
                  Day {index + 1}
                </div>
                <div className="grid grid-cols-1 items-center gap-6 md:grid-cols-3">
                  <Input
                    type="datetime-local"
                    label="Start Date"
                    value={formatForInput(slot.from_date)}
                    required
                    onChange={(e) =>
                      handleDateChange(index, "from_date", e.target.value)
                    }
                  />
                  <Input
                    type="datetime-local"
                    label="End Date"
                    value={formatForInput(slot.to_date)}
                    required
                    onChange={(e) =>
                      handleDateChange(index, "to_date", e.target.value)
                    }
                  />
                  <div className="flex justify-center md:justify-start">
                    <button
                      type="button"
                      onClick={() => removeDateSlot(index)}
                      className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-50 text-red-500 transition hover:bg-red-100"
                    >
                      <MdDelete size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <Section title="Event Media">
          <div className="md:col-span-2">
            <input
              type="file"
              multiple
              accept="image/*,video/*"
              onChange={handleMultimedia}
              className="w-full rounded-xl border p-3"
            />

            <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
              {formData.eventImage.map((img, i) => {
                const isExisting = typeof img === "string";
                const src = isExisting
                  ? getImageUrl(img)
                  : URL.createObjectURL(img);
                const isDefault = isDefaultMedia(img, i, "image");

                return (
                  <div
                    key={`img-${i}`}
                    className={`relative rounded-xl border p-2 transition ${
                      isDefault
                        ? "scale-[1.02] shadow-lg ring-2 ring-brand-500"
                        : "hover:shadow-md"
                    }`}
                  >
                    <img
                      src={src}
                      alt=""
                      className="h-32 w-full rounded-lg object-cover"
                    />
                    <div className="mt-2 flex justify-between">
                      {isDefault ? (
                        <span className="text-xs font-medium text-green-600">
                          Default Image
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() =>
                            setDefaultMedia({
                              type: isExisting ? "existing" : "new",
                              mediaType: "image",
                              value: isExisting ? img : i,
                            })
                          }
                          className="text-xs text-blue-600"
                        >
                          Set Default
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => removeImage(i)}
                        className="text-xs text-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}

              {formData.eventVideo.map((vid, i) => {
                const isExisting = typeof vid === "string";
                const src = isExisting
                  ? getImageUrl(vid)
                  : URL.createObjectURL(vid);
                const isDefault = isDefaultMedia(vid, i, "video");

                return (
                  <div
                    key={`vid-${i}`}
                    className={`relative rounded-xl border p-2 transition ${
                      isDefault
                        ? "scale-[1.02] shadow-lg ring-2 ring-brand-500"
                        : "hover:shadow-md"
                    }`}
                  >
                    <video
                      src={src}
                      controls
                      className="h-32 w-full rounded-lg object-cover"
                    />
                    <div className="mt-2 flex justify-between">
                      {isDefault ? (
                        <span className="text-xs font-medium text-green-600">
                          Default Video
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() =>
                            setDefaultMedia({
                              type: isExisting ? "existing" : "new",
                              mediaType: "video",
                              value: isExisting ? vid : i,
                            })
                          }
                          className="text-xs text-blue-600"
                        >
                          Set Default
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => removeVideo(i)}
                        className="text-xs text-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Section>

        <div className="flex justify-between">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="rounded-xl bg-red-500 px-6 py-3 text-white"
            >
              Back
            </button>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="rounded-xl bg-brand-500 px-6 py-3 text-white"
          >
            {initialData ? "Update Event" : "Create Event"}
          </button>
        </div>
      </form>
    </PageLayout>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h3 className="mb-4 text-lg font-semibold">{title}</h3>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">{children}</div>
    </section>
  );
}

function Select({ label, required, children, ...props }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select {...props} className="w-full rounded-xl border p-3">
        {children}
      </select>
    </div>
  );
}

function Input({ label, required, ...props }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input {...props} className="w-full rounded-xl border p-3" />
    </div>
  );
}
